import { useEffect, useRef, useState } from 'react';
import { feeConfigAPI, crdbAPI, paymentMethodsAPI } from '../../services/api';

const AGE_LABELS = { 1: '20 – 40 years', 2: '41 – 50 years', 3: '51 – 65 years' };

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );
}

export default function FeeConfig() {
  const [annualFees, setAnnualFees] = useState([]);
  const [payout, setPayout]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // Annual fee form state
  const [editingRow, setEditingRow]   = useState(null); // row being edited (or null = new)
  const [annualYear, setAnnualYear]   = useState(new Date().getFullYear());
  const [annualAmt, setAnnualAmt]     = useState('');
  const [annualNotes, setAnnualNotes] = useState('');
  const [annualSaving, setAnnualSaving] = useState(false);
  const [annualMsg, setAnnualMsg]     = useState({ text: '', ok: true });

  // Beneficiary payout form
  const [payoutAmt, setPayoutAmt]       = useState('');
  const [payoutNotes, setPayoutNotes]   = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutMsg, setPayoutMsg]       = useState({ text: '', ok: true });
  const [editingPayout, setEditingPayout] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  // Next-year early-open date
  const [openDate, setOpenDate]         = useState('');
  const [openDateSaving, setOpenDateSaving] = useState(false);
  const [openDateMsg, setOpenDateMsg]   = useState({ text: '', ok: true });
  const [currentFeeYear, setCurrentFeeYear] = useState(new Date().getFullYear());

  // Life membership
  const [lifeFees, setLifeFees]         = useState([]);
  const [lifeEdits, setLifeEdits]       = useState({});
  const [lifeSaving, setLifeSaving]     = useState(false);
  const [lifeMsg, setLifeMsg]           = useState({ text: '', ok: true });
  const [minYears, setMinYears]         = useState(10);

  // CRDB account mappings
  const [crdbAccounts, setCrdbAccounts]   = useState([]);
  const [crdbEditing, setCrdbEditing]     = useState(null); // id being edited
  const [crdbEditVal, setCrdbEditVal]     = useState({ crdb_number: '', description: '' });
  const [crdbSaving, setCrdbSaving]       = useState(false);
  const [crdbMsg, setCrdbMsg]             = useState({ text: '', ok: true });

  // Payment methods
  const [payMethods, setPayMethods]     = useState([]);
  const [pmUploading, setPmUploading]   = useState(null);
  const [pmToggling, setPmToggling]     = useState(null);
  const [pmEditing, setPmEditing]       = useState(null);   // id being edited
  const [pmEditVal, setPmEditVal]       = useState({});     // {name, dial, steps:[]}
  const [pmSaving, setPmSaving]         = useState(false);
  const [showAddPm, setShowAddPm]       = useState(false);
  const [newPm, setNewPm]               = useState({ name: '', dial: '', steps: [''] });
  const [addingPm, setAddingPm]         = useState(false);
  const fileInputRefs = useRef({});

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await feeConfigAPI.getAll();
      setAnnualFees(resp.annual_fees || []);
      setPayout(resp.beneficiary_payout || null);
      setCurrentFeeYear(resp.current_fee_year || new Date().getFullYear());
      const openResp = await feeConfigAPI.getNextYearOpenDate().catch(() => ({ next_year_fee_open_date: '' }));
      setOpenDate(openResp.next_year_fee_open_date || '');
      if (resp.beneficiary_payout) {
        setPayoutAmt(resp.beneficiary_payout.amount);
        setPayoutNotes(resp.beneficiary_payout.notes || '');
      }
      // Load life membership fees
      const lifeResp = await feeConfigAPI.getLifeMembershipFees();
      const fees = lifeResp.fees || [];
      setLifeFees(fees);
      const edits = {};
      fees.forEach(f => { edits[f.id] = { amount: f.amount, notes: f.notes || '' }; });
      setLifeEdits(edits);
      setMinYears(lifeResp.min_years ?? 10);
      // Load CRDB accounts
      const crdbResp = await crdbAPI.list();
      setCrdbAccounts(crdbResp.accounts || []);
      // Load payment methods
      const pmResp = await paymentMethodsAPI.all();
      setPayMethods(pmResp.methods || []);
    } catch {
      setError('Failed to load fee settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  function startEdit(row) {
    setEditingRow(row);
    setAnnualYear(row.fee_year);
    setAnnualAmt(row.amount);
    setAnnualNotes(row.notes || '');
    setAnnualMsg({ text: '', ok: true });
    document.getElementById('annual-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function cancelEdit() {
    setEditingRow(null);
    setAnnualYear(new Date().getFullYear());
    setAnnualAmt('');
    setAnnualNotes('');
    setAnnualMsg({ text: '', ok: true });
  }

  async function handleSaveAnnual(e) {
    e.preventDefault();
    setAnnualSaving(true);
    setAnnualMsg({ text: '', ok: true });
    try {
      await feeConfigAPI.saveAnnual({ fee_year: Number(annualYear), amount: Number(annualAmt), notes: annualNotes });
      setAnnualMsg({ text: editingRow ? '✓ Updated successfully' : '✓ Saved successfully', ok: true });
      cancelEdit();
      await load();
    } catch (err) {
      setAnnualMsg({ text: '✗ ' + (err.response?.data?.error || 'Save failed'), ok: false });
    } finally {
      setAnnualSaving(false);
    }
  }

  async function handleSavePayout(e) {
    e.preventDefault();
    setPayoutSaving(true);
    setPayoutMsg({ text: '', ok: true });
    try {
      await feeConfigAPI.saveBeneficiary({ amount: Number(payoutAmt), notes: payoutNotes });
      setPayoutMsg({ text: '✓ Saved successfully', ok: true });
      setEditingPayout(false);
      await load();
    } catch (err) {
      setPayoutMsg({ text: '✗ ' + (err.response?.data?.error || 'Save failed'), ok: false });
    } finally {
      setPayoutSaving(false);
    }
  }

  async function handleSaveOpenDate(e) {
    e.preventDefault();
    setOpenDateSaving(true);
    setOpenDateMsg({ text: '', ok: true });
    try {
      const res = await feeConfigAPI.saveNextYearOpenDate(openDate);
      setCurrentFeeYear(res.current_fee_year || currentFeeYear);
      setOpenDateMsg({ text: '✓ Saved successfully', ok: true });
    } catch (err) {
      setOpenDateMsg({ text: '✗ ' + (err.response?.data?.error || 'Save failed'), ok: false });
    } finally {
      setOpenDateSaving(false);
    }
  }

  async function handleDelete(id, year) {
    if (!window.confirm(`Delete the fee for year ${year}?`)) return;
    setDeletingId(id);
    try {
      await feeConfigAPI.delete(id);
      if (editingRow?.id === id) cancelEdit();
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSaveLife(e) {
    e.preventDefault();
    setLifeSaving(true); setLifeMsg({ text: '', ok: true });
    try {
      const brackets = lifeFees.map(f => ({
        id:     f.id,
        amount: Number(lifeEdits[f.id]?.amount ?? f.amount),
        notes:  lifeEdits[f.id]?.notes ?? f.notes,
      }));
      await feeConfigAPI.saveLifeMembershipFees(brackets, Number(minYears));
      setLifeMsg({ text: '✓ Saved successfully', ok: true });
      await load();
    } catch (err) {
      setLifeMsg({ text: '✗ ' + (err.response?.data?.error || 'Save failed'), ok: false });
    } finally {
      setLifeSaving(false);
    }
  }

  async function handleSaveCrdb(id) {
    setCrdbSaving(true); setCrdbMsg({ text: '', ok: true });
    try {
      await crdbAPI.update(id, crdbEditVal);
      setCrdbMsg({ text: '✓ Updated', ok: true });
      setCrdbEditing(null);
      const r = await crdbAPI.list();
      setCrdbAccounts(r.accounts || []);
    } catch (err) {
      setCrdbMsg({ text: '✗ ' + (err.response?.data?.error || 'Failed'), ok: false });
    } finally {
      setCrdbSaving(false); }
  }

  if (loading) return <p className="text-slate-400 p-6">Loading...</p>;

  return (
    <div className="max-w-3xl mx-auto space-y-8 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Fee Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage annual member contributions and beneficiary payouts.</p>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {/* ── Annual Contribution ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Annual Contribution</h2>
          <p className="text-sm text-slate-500 mt-0.5">The amount each member is required to pay per year.</p>
        </div>

        {annualFees.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold text-slate-500">Year</th>
                  <th className="px-5 py-3 text-right font-semibold text-slate-500">Amount (TZS)</th>
                  <th className="px-5 py-3 text-left font-semibold text-slate-500">Notes</th>
                  <th className="px-5 py-3 text-right font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {annualFees.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-slate-50/60 transition-colors ${editingRow?.id === row.id ? 'bg-orange-50' : ''}`}
                  >
                    <td className="px-5 py-3 font-bold text-slate-900">{row.fee_year}</td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-700">
                      {Number(row.amount).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-slate-500">{row.notes || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(row)}
                          className="inline-flex items-center gap-1 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-600 hover:bg-orange-100 transition-colors"
                        >
                          <PencilIcon /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(row.id, row.fee_year)}
                          disabled={deletingId === row.id}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
                        >
                          {deletingId === row.id ? '...' : <><TrashIcon /> Delete</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-6 py-4 text-sm text-slate-400">No annual fees configured yet.</p>
        )}

        {/* Add / Edit form */}
        <div id="annual-form" className="px-6 py-5 border-t border-slate-100 bg-slate-50/40">
          <p className="text-sm font-semibold text-slate-700 mb-4">
            {editingRow ? `Editing fee for year ${editingRow.fee_year}` : 'Add / Update Annual Fee'}
          </p>
          <form onSubmit={handleSaveAnnual} className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Year</label>
              <input
                type="number" min="2000" max="2100" required
                value={annualYear}
                onChange={(e) => setAnnualYear(e.target.value)}
                disabled={!!editingRow}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (TZS)</label>
              <input
                type="number" min="1" required
                placeholder="e.g. 50000"
                value={annualAmt}
                onChange={(e) => setAnnualAmt(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optional)</label>
              <input
                type="text"
                placeholder="e.g. AWTF contribution 2027"
                value={annualNotes}
                onChange={(e) => setAnnualNotes(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="sm:col-span-3 flex flex-wrap items-center gap-3">
              <button
                type="submit" disabled={annualSaving}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-6 py-2.5 text-sm transition-colors"
              >
                {annualSaving ? 'Saving...' : editingRow ? 'Update Fee' : 'Save Fee'}
              </button>
              {editingRow && (
                <button
                  type="button" onClick={cancelEdit}
                  className="rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-medium px-5 py-2.5 text-sm transition-colors"
                >
                  Cancel
                </button>
              )}
              {annualMsg.text && (
                <span className={`text-sm font-semibold ${annualMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                  {annualMsg.text}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* ── Open Next Year's Fee Early ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Open Next Year's Fee Early</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Members currently see and pay <strong>{currentFeeYear}</strong>'s annual fee. Set a date below <strong>in the
            current year ({new Date().getFullYear()})</strong> to let them start seeing and paying next year's fee ahead
            of 1 January — e.g. setting {new Date().getFullYear()}-11-01 opens {new Date().getFullYear() + 1}'s fee from
            that date. Make sure {currentFeeYear + 1}'s amount is configured above before opening it.
          </p>
        </div>
        <form onSubmit={handleSaveOpenDate} className="px-6 py-5 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Open date</label>
            <input
              type="date"
              value={openDate}
              onChange={(e) => setOpenDate(e.target.value)}
              className="rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button type="submit" disabled={openDateSaving}
            className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2.5 text-sm font-semibold">
            {openDateSaving ? 'Saving…' : 'Save'}
          </button>
          {openDate && (
            <button type="button" onClick={() => { setOpenDate(''); }}
              className="text-xs font-semibold text-slate-500 hover:text-red-600">
              Clear (disable early opening)
            </button>
          )}
          {openDateMsg.text && (
            <span className={`text-sm font-semibold ${openDateMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
              {openDateMsg.text}
            </span>
          )}
        </form>
        {openDate && (() => {
          const todayStr   = new Date().toISOString().slice(0, 10);
          const calYear    = new Date().getFullYear();
          const openYear   = Number(openDate.slice(0, 4));
          const targetYear = Math.max(calYear, openYear + 1);
          const active     = todayStr >= openDate;
          const mismatched = openYear !== calYear;
          return (
            <div className={`mx-6 mb-5 rounded-xl px-4 py-3 text-xs leading-relaxed ${mismatched ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-slate-50 border border-slate-200 text-slate-600'}`}>
              {active
                ? <>✓ With this date, members would see <strong>{targetYear}</strong>'s fee <strong>right now</strong> (the date has already passed).</>
                : <>With this date, members will start seeing <strong>{targetYear}</strong>'s fee from <strong>{openDate}</strong> onward.</>}
              {mismatched && (
                <div className="mt-1 font-semibold">
                  ⚠ This date is in {openYear}, not the current year ({calYear}) — it only takes effect once {openYear} actually arrives,
                  and will then open {openYear + 1} (not {calYear + 1}). To open {calYear + 1}'s fee now or this year, use a {calYear}-dated date instead.
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── Beneficiary Payout (year-aware) ── */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm font-semibold text-amber-800">Beneficiary Payout by Year</p>
        <p className="text-xs text-amber-700 mt-0.5">
          Current (no year): <strong>TZS {Number(payout?.amount || 0).toLocaleString()}</strong> &nbsp;|&nbsp;
          From 2027: <strong>TZS 10,000,000</strong> (set per AGM resolution). Claims use the year-specific amount automatically.
        </p>
      </div>

      {/* ── Beneficiary Payout ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Beneficiary Payout</h2>
            <p className="text-sm text-slate-500 mt-0.5">Amount paid to each beneficiary when a member passes away.</p>
          </div>
          {payout && !editingPayout && (
            <button
              onClick={() => setEditingPayout(true)}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-600 hover:bg-orange-100 transition-colors mt-1"
            >
              <PencilIcon /> Edit
            </button>
          )}
        </div>

        {payout && !editingPayout && (
          <div className="px-6 py-5 flex items-center gap-6">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Current payout</p>
              <p className="text-3xl font-bold text-emerald-700">TZS {Number(payout.amount).toLocaleString()}</p>
            </div>
            {payout.notes && <p className="text-sm text-slate-500 border-l border-slate-200 pl-6">{payout.notes}</p>}
          </div>
        )}

        {(!payout || editingPayout) && (
          <div className="px-6 py-5">
            {!payout && <p className="text-sm text-slate-400 mb-4">No payout configured yet.</p>}
            <form onSubmit={handleSavePayout} className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (TZS)</label>
                <input
                  type="number" min="1" required
                  placeholder="e.g. 240000"
                  value={payoutAmt}
                  onChange={(e) => setPayoutAmt(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Revised March 2026"
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                <button
                  type="submit" disabled={payoutSaving}
                  className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-6 py-2.5 text-sm transition-colors"
                >
                  {payoutSaving ? 'Saving...' : payout ? 'Update Payout' : 'Save Payout'}
                </button>
                {editingPayout && (
                  <button
                    type="button" onClick={() => { setEditingPayout(false); setPayoutMsg({ text: '', ok: true }); }}
                    className="rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-medium px-5 py-2.5 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                )}
                {payoutMsg.text && (
                  <span className={`text-sm font-semibold ${payoutMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                    {payoutMsg.text}
                  </span>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
      {/* ── Life Membership Fees ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Life Membership — Lump Sum Fees</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Members can pay a one-time lump sum to become life members (exempt from annual contributions).
            Approved at AGM 1st May 2026.
          </p>
        </div>

        {lifeFees.length === 0 ? (
          <p className="px-6 py-4 text-sm text-slate-400">No life membership brackets configured.</p>
        ) : (
          <form onSubmit={handleSaveLife} className="p-6 space-y-4">
            <div className="max-w-xs rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <label className="block text-xs font-semibold text-amber-700 mb-1">
                Minimum years of AWTF contribution required
              </label>
              <input
                type="number" min="1" required
                value={minYears}
                onChange={e => setMinYears(e.target.value)}
                className="w-full rounded-xl border border-amber-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <p className="mt-1.5 text-[11px] text-amber-600">
                An age-eligible member who has paid fewer years than this must top up the shortfall
                (at each missing year's actual historical rate) before they can enroll as a Life Member.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {lifeFees.map((f, idx) => (
                <div key={f.id} className="rounded-2xl border border-violet-200 bg-violet-50 p-4 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-violet-500 font-semibold">Age Group</p>
                    <p className="text-base font-bold text-violet-900 mt-0.5">{f.age_from} – {f.age_to} years</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (TZS)</label>
                    <input
                      type="number" min="1" required
                      value={lifeEdits[f.id]?.amount ?? f.amount}
                      onChange={e => setLifeEdits(prev => ({ ...prev, [f.id]: { ...prev[f.id], amount: e.target.value } }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Notes</label>
                    <input
                      type="text"
                      value={lifeEdits[f.id]?.notes ?? (f.notes || '')}
                      onChange={e => setLifeEdits(prev => ({ ...prev, [f.id]: { ...prev[f.id], notes: e.target.value } }))}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit" disabled={lifeSaving}
                className="rounded-xl bg-violet-700 hover:bg-violet-800 disabled:opacity-60 text-white font-semibold px-6 py-2.5 text-sm transition-colors">
                {lifeSaving ? 'Saving…' : 'Save Life Membership Fees'}
              </button>
              {lifeMsg.text && (
                <span className={`text-sm font-semibold ${lifeMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                  {lifeMsg.text}
                </span>
              )}
            </div>
          </form>
        )}
      </div>

      {/* ── CRDB Account Mappings ── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold text-slate-800">CRDB Account Mappings</h2>
          {crdbMsg.text && (
            <span className={`text-xs font-semibold ${crdbMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
              {crdbMsg.text}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Account codes used by CRDB to categorise payments. The <strong>Code</strong> matches the
          <code className="mx-1 rounded bg-slate-100 px-1 text-[11px]">account</code> column in invoices.
        </p>

        {/* HQ accounts */}
        {['hq', 'organiser'].map(type => (
          <div key={type} className="mb-6">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {type === 'hq' ? 'TLS HQ Accounts' : 'Chapter / Organiser Accounts'}
            </p>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 w-24">Code</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 w-16">CRDB #</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Description</th>
                    <th className="px-3 py-2 w-20" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {crdbAccounts.filter(a => a.account_type === type).map(a => (
                    <tr key={a.id} className={crdbEditing === a.id ? 'bg-blue-50' : 'hover:bg-slate-50'}>
                      <td className="px-3 py-2 font-mono font-bold text-slate-800">{a.account_code}</td>
                      {crdbEditing === a.id ? (
                        <>
                          <td className="px-2 py-1.5">
                            <input value={crdbEditVal.crdb_number}
                              onChange={e => setCrdbEditVal(v => ({ ...v, crdb_number: e.target.value }))}
                              className="w-14 rounded-lg border border-blue-300 px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-400" />
                          </td>
                          <td className="px-2 py-1.5">
                            <input value={crdbEditVal.description}
                              onChange={e => setCrdbEditVal(v => ({ ...v, description: e.target.value }))}
                              className="w-full rounded-lg border border-blue-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => handleSaveCrdb(a.id)} disabled={crdbSaving}
                                className="rounded-lg bg-blue-600 text-white text-xs font-semibold px-2.5 py-1 hover:bg-blue-700 disabled:opacity-50">
                                {crdbSaving ? '…' : 'Save'}
                              </button>
                              <button onClick={() => setCrdbEditing(null)}
                                className="rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-1 hover:bg-slate-200">
                                ✕
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 font-mono text-slate-700">{a.crdb_number}</td>
                          <td className="px-3 py-2 text-slate-600">{a.description}</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => {
                              setCrdbEditing(a.id);
                              setCrdbEditVal({ crdb_number: a.crdb_number, description: a.description });
                              setCrdbMsg({ text: '', ok: true });
                            }} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200">
                              Edit
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* ── Payment Methods ── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold text-slate-800">Payment Methods</h2>
          <button onClick={() => { setShowAddPm(v => !v); setNewPm({ name: '', dial: '', steps: [''] }); }}
            className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 transition-colors">
            + Add Method
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Upload logos and set step-by-step payment instructions shown to members in the "How to Pay" modal.
        </p>

        {/* Add new method form */}
        {showAddPm && (
          <div className="mb-6 rounded-2xl border-2 border-blue-200 bg-blue-50 p-5 space-y-4">
            <p className="text-sm font-bold text-blue-800">New Payment Method</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Name</label>
                <input value={newPm.name} onChange={e => setNewPm(v => ({ ...v, name: e.target.value }))}
                  placeholder="e.g. M-Pesa"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Dial / Platform</label>
                <input value={newPm.dial} onChange={e => setNewPm(v => ({ ...v, dial: e.target.value }))}
                  placeholder="e.g. *150*00# or App"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-2 block">Steps (how to pay)</label>
              <div className="space-y-2">
                {newPm.steps.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="shrink-0 h-5 w-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <input value={s} onChange={e => setNewPm(v => ({ ...v, steps: v.steps.map((x, j) => j === i ? e.target.value : x) }))}
                      placeholder={`Step ${i + 1}`}
                      className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    {newPm.steps.length > 1 && (
                      <button onClick={() => setNewPm(v => ({ ...v, steps: v.steps.filter((_, j) => j !== i) }))}
                        className="text-red-400 hover:text-red-600 font-bold text-lg leading-none">×</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setNewPm(v => ({ ...v, steps: [...v.steps, ''] }))}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold">+ Add step</button>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button disabled={addingPm || !newPm.name.trim()}
                onClick={async () => {
                  setAddingPm(true);
                  try {
                    await paymentMethodsAPI.create({ name: newPm.name.trim(), dial: newPm.dial.trim(), steps: newPm.steps.filter(Boolean) });
                    const r = await paymentMethodsAPI.all();
                    setPayMethods(r.methods || []);
                    setShowAddPm(false);
                  } catch { /* silent */ }
                  finally { setAddingPm(false); }
                }}
                className="rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2">
                {addingPm ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setShowAddPm(false)}
                className="rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold px-4 py-2 hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Methods list */}
        <div className="space-y-4">
          {payMethods.map(m => (
            <div key={m.id} className={`rounded-2xl border-2 transition-opacity ${m.is_active ? 'border-slate-200' : 'border-dashed border-slate-200 opacity-60'}`}>
              {/* Top row: logo + name + buttons */}
              <div className="flex items-center gap-4 p-4">
                <div className="h-12 w-20 shrink-0 flex items-center justify-center rounded-xl bg-slate-50 overflow-hidden border border-slate-100">
                  {m.logo_url
                    ? <img src={m.logo_url} alt={m.name} className="max-h-10 max-w-full object-contain" />
                    : <span className="text-[10px] text-slate-400">No logo</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-sm">{m.name}</p>
                  {m.dial && <p className="text-xs text-slate-500 font-mono mt-0.5">{m.dial}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">{(m.steps || []).length} step{(m.steps || []).length !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Hidden file input */}
                  <input type="file" accept="image/*" className="hidden"
                    ref={el => { fileInputRefs.current[m.id] = el; }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      setPmUploading(m.id);
                      try { await paymentMethodsAPI.uploadLogo(m.id, file); const r = await paymentMethodsAPI.all(); setPayMethods(r.methods || []); }
                      catch { /* silent */ } finally { setPmUploading(null); e.target.value = ''; }
                    }} />
                  <button disabled={pmUploading === m.id} onClick={() => fileInputRefs.current[m.id]?.click()}
                    className="rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 text-xs font-semibold px-3 py-1.5">
                    {pmUploading === m.id ? '…' : (m.logo_url ? '🖼 Logo' : '+ Logo')}
                  </button>
                  <button onClick={() => {
                    if (pmEditing === m.id) { setPmEditing(null); return; }
                    setPmEditing(m.id);
                    setPmEditVal({ name: m.name, dial: m.dial || '', steps: m.steps?.length ? [...m.steps] : [''] });
                  }}
                    className={`rounded-xl text-xs font-semibold px-3 py-1.5 transition-colors ${pmEditing === m.id ? 'bg-slate-800 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
                    {pmEditing === m.id ? 'Close' : 'Edit'}
                  </button>
                  <button disabled={pmToggling === m.id}
                    onClick={async () => {
                      setPmToggling(m.id);
                      try { await paymentMethodsAPI.update(m.id, { is_active: m.is_active ? 0 : 1 }); const r = await paymentMethodsAPI.all(); setPayMethods(r.methods || []); }
                      catch { /* silent */ } finally { setPmToggling(null); }
                    }}
                    className={`rounded-xl text-xs font-semibold px-3 py-1.5 border transition-colors ${m.is_active ? 'border-slate-200 text-slate-500 hover:bg-slate-50' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50'}`}>
                    {pmToggling === m.id ? '…' : (m.is_active ? 'Hide' : 'Show')}
                  </button>
                </div>
              </div>

              {/* Edit panel */}
              {pmEditing === m.id && (
                <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50 rounded-b-2xl">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Name</label>
                      <input value={pmEditVal.name} onChange={e => setPmEditVal(v => ({ ...v, name: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Dial / Platform</label>
                      <input value={pmEditVal.dial} onChange={e => setPmEditVal(v => ({ ...v, dial: e.target.value }))}
                        placeholder="e.g. *150*00# or App"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-2 block">Steps (how to pay)</label>
                    <div className="space-y-2">
                      {pmEditVal.steps.map((s, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <span className="shrink-0 h-5 w-5 rounded-full bg-slate-700 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                          <input value={s}
                            onChange={e => setPmEditVal(v => ({ ...v, steps: v.steps.map((x, j) => j === i ? e.target.value : x) }))}
                            className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                          {pmEditVal.steps.length > 1 && (
                            <button onClick={() => setPmEditVal(v => ({ ...v, steps: v.steps.filter((_, j) => j !== i) }))}
                              className="text-red-400 hover:text-red-600 font-bold text-lg leading-none">×</button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => setPmEditVal(v => ({ ...v, steps: [...v.steps, ''] }))}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold">+ Add step</button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button disabled={pmSaving}
                      onClick={async () => {
                        setPmSaving(true);
                        try {
                          await paymentMethodsAPI.update(m.id, { name: pmEditVal.name.trim(), dial: pmEditVal.dial.trim(), steps: pmEditVal.steps.filter(Boolean) });
                          const r = await paymentMethodsAPI.all(); setPayMethods(r.methods || []);
                          setPmEditing(null);
                        } catch { /* silent */ } finally { setPmSaving(false); }
                      }}
                      className="rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2">
                      {pmSaving ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button onClick={() => setPmEditing(null)}
                      className="rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold px-4 py-2 hover:bg-slate-100">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
